import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter, HostListener,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {MatIconModule} from "@angular/material/icon";
import {MatCardModule} from "@angular/material/card";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatButtonModule} from "@angular/material/button";
import {DOCUMENT, JsonPipe, NgForOf, NgIf, NgStyle} from "@angular/common";
import {HttpClient} from "@angular/common/http";
import {ActivatedRoute, Router, RouterLink, NavigationStart} from "@angular/router";
import {MatBottomSheet} from "@angular/material/bottom-sheet";
import {Title} from "@angular/platform-browser";
import {hymnsDetailed} from "../hymn/hymnsDetailed";
import {MidiPlayerService} from "../service/midi-player.service";
import {LayoutService} from "../service/layout.service";
import {ShareService} from "../service/share.service";
import {HymnSelectionService} from "../service/hymn-selection.service";
import {MatMenuModule} from "@angular/material/menu";
import {SidenavService} from "../service/sidenav.service";
import {FontSizeComponent} from "../controls/font-size/font-size.component";
import {SettingsService} from "../service/settings.service";
import {ContinuousPlaybackService} from "../service/continuous-playback.service";
import {AutoScrollService} from "../service/auto-scroll.service";
import {MusicControlComponent} from "../controls/music-control/music-control.component";
import {ModalService} from "../service/modal.service";
import {Hymn} from "../hymn/hymn";
import {translation} from "../translation";
import {DoubleTapDirective} from "../directive/double-tap.directive";
import {SwipeGestureDirective} from "../directive/swipe-gesture.directive";

@Component({
  selector: 'app-hymn-reader',
  standalone: true,
  imports: [
    MatIconModule,
    MatCardModule,
    MatToolbarModule,
    MatButtonModule,
    MatMenuModule,
    RouterLink,
    NgIf,
    NgForOf,
    NgStyle,
    MusicControlComponent,
    JsonPipe,
    DoubleTapDirective,
    SwipeGestureDirective,
  ],
  templateUrl: './hymn-reader.component.html',
  styleUrl: './hymn-reader.component.scss'
})
export class HymnReaderComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('headerChorus') public headerChorus!: ElementRef;
  @ViewChild('headerContent') public headerContent!: ElementRef;
  @ViewChild('chorus') public chorus!: ElementRef;
  @ViewChild('content_container') public content!: ElementRef;
  @ViewChild('scrollWrapper') public scrollWrapper!: ElementRef;

  hymn: any = null;
  elem: any;
  fontSizeBase = this.settingService.getReadModeFontSize();
  fontSize = this.fontSizeBase;
  fontSizeStyle = this.calculateFontSizeStyle();
  fontSizeMax= 100;
  fontSizeMin = 8;
  lineHeightMultiplier = 1.5;
  lineHeightStyle = this.calculateLineHeightStyle();
  lastHymnNumber = hymnsDetailed[hymnsDetailed.length - 1].hymnNumber;

  // Font family and weight for read mode
  readFontFamily: string | undefined = this.settingService.getFontFamily();
  readFontWeight: number | undefined = this.settingService.getFontWeight();

  // Pointer events state
  private swipeStartX = 0;
  private swipeStartY = 0;
  private swipeStartTime = 0;
  private activePointers = new Map<number, PointerEvent>();

  // Bound handlers for add/remove
  private peDown?: (ev: PointerEvent) => void;
  private peMove?: (ev: PointerEvent) => void;
  private peUpCancel?: (ev: PointerEvent) => void;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private http: HttpClient,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private bottomSheet: MatBottomSheet,
    private titleService: Title,
    public router: Router,
    public midiService: MidiPlayerService,
    public hymnSelectionService: HymnSelectionService,
    public layoutService: LayoutService,
    public shareService: ShareService,
    public sidenavService: SidenavService,
    protected settingService: SettingsService,
    protected autoScrollService: AutoScrollService,
    protected modalService: ModalService,
    protected continuousPlaybackService: ContinuousPlaybackService) {
      settingService.readModeFontSizeSubject.subscribe(() => {
        this.ngZone.run(() => {
          this.fontSizeBase = this.settingService.getReadModeFontSize();
          this.fontSize = this.settingService.getReadModeFontSize();
          this.fontSizeStyle = this.calculateFontSizeStyle();
          this.lineHeightStyle = this.calculateLineHeightStyle();
        });
      });

      // Subscribe to global font family changes
      settingService.fontFamilySubject.subscribe((f) => {
        this.ngZone.run(() => {
          this.readFontFamily = f;
        });
      });

      // Subscribe to global font weight changes
      settingService.fontWeightSubject.subscribe((w) => {
        this.ngZone.run(() => {
          this.readFontWeight = (w ?? 400);
        });
      });
  }

  ngAfterViewInit(): void {
    // Gesture handling is delegated to directives (swipeGesture)
    // Ensure scroll wrapper allows vertical panning via CSS
    const el = this.scrollWrapper?.nativeElement as HTMLElement | undefined;
    if (el && (!el.style.touchAction || el.style.touchAction === 'auto')) {
      el.style.touchAction = 'pan-y';
    }
  }

  getLastHymn() {
    if(this.settingService.getHistory() && this.settingService.getHistory().length > 0){
      return this.settingService.getHistory()[0];
    } else {
      return hymnsDetailed[Math.floor(Math.random() * hymnsDetailed.length)];
    }
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    this.ngZone.run(() => {
      if (!this.hymnSelectionService.searchFocus && !this.hymnSelectionService.dialpadActive) {
        switch (event.key) {
          case 'ArrowLeft':
          case 'b':
            this.onSwipePrevious();
            break;
          case 'ArrowRight':
          case 'n':
            this.onSwipeNext();
            break;
          case 'd':
          case 'D':
            this.hymnSelectionService.dialpad();
            break;
          case 'l':
          case 'L':
            this.sidenavService.toggleList();
            break;
          case 'w':
          case 'W':
            this.hymnSelectionService.goToRandomHymn(false);
            break;
          case 'f':
          case 'F':
            this.layoutService.fullscreen ?
              this.layoutService.exitFullScreen() : this.layoutService.enterFullScreen();
            break;
          case 'g':
          case 'G':
            this.hymnSelectionService.goToRandomHymn(true);
            break;
          case 's':
          case 'S':
            if(this.midiService.playing) {
              this.midiService.stop()
            } else {
              if(this.autoScrollService.enabled) {
                this.autoScrollService.startAutoScroll();
              }
              this.midiService.playMidi(this.hymn, this.settingService.getInstruments(), this.hymn.verses.length);
            }
            break;
          case 'a':
          case 'A':
            this.autoScrollService.active? this.autoScrollService.stopAutoScroll() : this.autoScrollService.startAutoScroll();
            break;
          case 'v':
          case 'V':
            this.router.navigate([`/${translation.path_present}/`+this.hymn.hymnNumber]);
            break;
          case '+':
            if(this.fontSize < this.fontSizeMax) {
              this.updateFontSize(this.fontSize + 1);
            }
            break;
          case '-':
            if(this.fontSize > this.fontSizeMin) {
              this.updateFontSize(this.fontSize - 1);
            }
            break;
          case '<':
          case ',':
            this.midiService.decreasePlaybackSpeed();
            break;
          case '>':
          case '.':
            this.midiService.increasePlaybackSpeed();
            break;
          case 'm':
            this.midiService.setPlaybackSpeed(1.0);
            break;
          case 'i':
            if(!this.modalService.active) {
              this.modalService.openDialog('shortcuts')
            } else {
              this.modalService.closeDialog();
            }
            break;
          case 'Escape':
            if(this.modalService.active) {
              this.modalService.closeDialog();
            }
            break;
        }
      } else {
        switch (event.key) {
          case 'd':
          case 'D':
            this.hymnSelectionService.dialpadCancel();
            break;
          case 'Escape':
            this.hymnSelectionService.dialpadCancel();
            this.hymnSelectionService.searchFocus = false;
            break;
        }
      }
    });
  }


  calculateFontSizeStyle() {
    return this.fontSize + 'px';
  }

  calculateLineHeightStyle() {
    return this.fontSize * this.lineHeightMultiplier + 'px';
  }

  toggleListSidenav() {
    this.sidenavService.toggleList();
  }

  toggleSettingSidenav() {
    this.settingService.toggleSettings();
  }

  ngOnInit() {
    this.settingService.setHymnViewMode();
    this.elem = document.documentElement;

    // Ensure any open bottom sheet is closed on navigation to prevent scroll lock
    this.router.events.subscribe(ev => {
      if (ev instanceof NavigationStart) {
        this.bottomSheet.dismiss();
      }
    });

    this.route.paramMap.subscribe(params => {
      if (params.get('reference')) {
        setTimeout(() => {
          if(this.layoutService.singleView) {
            this.sidenavService.closeList();
          }
        }, 10);
        this.getJSON(params.get('reference')!);
        this.hymnSelectionService.selectedHymn = this.hymn;
        this.settingService.addToHistory(this.hymn.hymnNumber);
      } else {
        this.hymn = this.getLastHymn();
        this.hymnSelectionService.selectedHymn = this.hymn;
      }
      this.titleService.setTitle(`${translation.hymn} ${this.hymn.hymnNumber} - ${this.hymn.title} - ${translation.title_full}`);
      if(!this.continuousPlaybackService.active) {
        this.midiService.stop();
        this.autoScrollService.stopAutoScroll();
      }
    });
  }

  ngOnDestroy(): void {
    if (!this.continuousPlaybackService.active) {
      this.midiService.stop();
      this.autoScrollService.stopAutoScroll();
    }
  }

  public getJSON(reference: string) {
    this.hymn = hymnsDetailed[+reference - 1];
  }

  onSwipePrevious() {
    // Normally you cannot swipe past the first hymn, but in continuous playback mode hymns are shuffled and repeated
    if (this.continuousPlaybackService.active || this.hymn.hymnNumber > 1) {
      if (!this.continuousPlaybackService.active) {
        this.ngZone.run(() => this.router.navigate([`${translation.path_hymn}/` + (this.hymn.hymnNumber - 1)])).then();
      } else {
        this.continuousPlaybackService.previous();
      }
    }
  }

  onSwipeNext() {
    // Normally you cannot swipe past the last hymn, but in continuous playback mode hymns are shuffled and repeated
    if (this.continuousPlaybackService.active || this.hymn.hymnNumber < this.lastHymnNumber) {
      if (!this.continuousPlaybackService.active) {
        this.ngZone.run(() => this.router.navigate([translation.path_hymn + '/' + (this.hymn.hymnNumber + 1)])).then();
      } else {
        this.continuousPlaybackService.next();
      }
    }
  }


  changeFontSize() {
    this.layoutService.fontSizeSheetActive = true;

    const bottomSheetRef = this.bottomSheet.open(FontSizeComponent, {
      data: {
        // visibility flags
        fontSizeEnabled: true,
        linesPerVerseEnabled: false,
        // font size slider params
        fontSizeMin: this.fontSizeMin,
        fontSizeMax: this.fontSizeMax,
        currentFontSize: this.fontSize,
        fontSizeChange: new EventEmitter<number>(),
        // font family controls
        fonts: this.settingService.getAvailableFonts(),
        currentFontFamily: this.settingService.getFontFamily(),
        fontFamilyChange: new EventEmitter<string | undefined>(),
        // font weight controls
        fontWeightEnabled: true,
        currentFontWeight: this.settingService.getFontWeight(),
        fontWeightChange: new EventEmitter<number | undefined>(),
        // text outline toggle (global)
        textOutlineToggleEnabled: true,
        textOutlineEnabled: this.settingService.getTextOutlineEnabled(),
        textOutlineChange: new EventEmitter<boolean>(),
        // text alignment (read mode)
        textAlignmentEnabled: true,
        currentTextAlignment: this.settingService.getTextAlignmentReadMode(),
        textAlignmentChange: new EventEmitter<string>()
      },
    });

    bottomSheetRef.instance.data.fontSizeChange?.subscribe((newFontSize: number) => {
      this.updateFontSize(newFontSize);
    });

    // font family change
    bottomSheetRef.instance.data.fontFamilyChange?.subscribe((fam: string | undefined) => {
      this.settingService.setFontFamily(fam);
    });

    // font weight change
    bottomSheetRef.instance.data.fontWeightChange?.subscribe((w: number | undefined) => {
      this.settingService.setFontWeight(w);
    });

    // text outline toggle
    bottomSheetRef.instance.data.textOutlineChange?.subscribe((enabled: boolean) => {
      this.settingService.setTextOutlineEnabled(enabled);
    });

    // text alignment change
    bottomSheetRef.instance.data.textAlignmentChange?.subscribe((al: string) => {
      this.settingService.setTextAlignmentReadMode(al);
    });

    bottomSheetRef.afterDismissed().subscribe(() => {
      this.layoutService.fontSizeSheetActive = false;
    });
  }

  updateFontSize(fontSize: number) {
    this.fontSizeBase = fontSize;
    this.settingService.setFontSize(fontSize);
    this.fontSize = fontSize;
    this.fontSizeStyle = this.calculateFontSizeStyle();
    this.lineHeightStyle = this.calculateLineHeightStyle();
  }

  protected readonly Hymn = Hymn;
  protected readonly translation = translation;
  protected readonly hymnsDetailed = hymnsDetailed;

  async onDoubleTap() {
    if(this.layoutService.fullscreenSupported()) {
      await this.layoutService.toggleFullScreen();
    }
  }
}
